/**
 * Module-level cache for agency student detail objects fetched from
 * /agency/students/<memberId>/.
 *
 * Storing this outside React state ensures the cache survives navigation
 * without requiring a context provider.
 *
 * TTL is 30 seconds. After expiry, AgencyStudentDetailView falls back to a
 * fresh API call.
 *
 * Type definitions for StudentDetail and its sub-types are co-located here so
 * both the cache and the view component share a single source of truth.
 */

export interface MemberInfo {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    citizenship: string;
    gender: string;
    institution_name: string;
}

export interface DocumentItem {
    id: number;
    document_type: string;
    file_name: string;
    status: string;
    required: boolean;
}

export interface ApplicationItem {
    application_number: string;
    applied_program_name: string;
    institution_name: string;
    stage: string;
    status: string;
    applied_term_name: string;
}

export interface StudentDetail {
    member: MemberInfo;
    documents: DocumentItem[];
    applications: ApplicationItem[];
}

interface CacheEntry {
    detail: StudentDetail;
    fetchedAt: number;
}

const CACHE_TTL_MS = 30_000;

const cache = new Map<string, CacheEntry>();

if (import.meta.env.DEV) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).__agencyStudentDetailCache = cache;
}

/**
 * Returns the cached student detail for the given member ID along with the
 * timestamp it was fetched, or null if the entry is missing or expired.
 */
export function getCachedAgencyStudentDetail(
    memberId: string,
): { detail: StudentDetail; fetchedAt: number } | null {
    const entry = cache.get(memberId);
    if (!entry) return null;
    if (Date.now() - entry.fetchedAt > CACHE_TTL_MS) {
        cache.delete(memberId);
        return null;
    }
    return { detail: entry.detail, fetchedAt: entry.fetchedAt };
}

/**
 * Stores the full student detail object. Called by AgencyStudentDetailView
 * after a successful fetch or manual refresh.
 */
export function setCachedAgencyStudentDetail(memberId: string, detail: StudentDetail): void {
    cache.set(memberId, { detail, fetchedAt: Date.now() });
}

/**
 * Forcefully removes a cached entry so the next visit triggers a fresh fetch.
 */
export function invalidateCachedAgencyStudentDetail(memberId: string): void {
    cache.delete(memberId);
}
